import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Check, X } from 'lucide-react';

export interface WizardStep {
  id: string;
  title: string;
  description: string;
  component: React.ComponentType<any>;
  isValid?: boolean;
}

const MoodleWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardData, setWizardData] = useState({
    theme: null,
    institutionInfo: {},
    courses: [],
    users: [],
    plugins: []
  });

  // Importamos los componentes de pasos dinámicamente
  const steps: WizardStep[] = [
    {
      id: 'theme-selection',
      title: 'Selecciona tu Tema',
      description: 'Elige el diseño visual para tu plataforma Moodle',
      component: React.lazy(() => import('../components/wizard/steps/ThemeSelectionStep'))
    },
    {
      id: 'institution-info',
      title: 'Información Institucional',
      description: 'Configura los datos básicos de tu institución',
      component: React.lazy(() => import('../components/wizard/steps/InstitutionInfoStep'))
    },
    {
      id: 'admin-info',
      title: 'Información del Administrador',
      description: 'Proporciona tus datos de contacto',
      component: React.lazy(() => import('../components/wizard/steps/CourseStructureStep'))
    },
    {
      id: 'review-launch',
      title: 'Revisar y Lanzar',
      description: 'Confirma tu configuración y crea tu Moodle',
      component: React.lazy(() => import('../components/wizard/steps/ReviewLaunchStep'))
    }
  ];

  const updateWizardData = (stepData: any) => {
    setWizardData(prev => ({ ...prev, ...stepData }));
  };

  const goToNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleComplete = () => {
    console.log('Configuración del wizard:', wizardData);
    // Aquí puedes enviar la configuración al backend
    navigate('/');
  };

  const handleCancel = () => {
    navigate('/');
  };

  const CurrentStepComponent = steps[currentStep]?.component;

  return (
    <div className="wizard-page">
      {/* Progress Bar */}
      <div className="wizard-page-progress">
        <div className="container">
          <div className="wizard-page-progress-header">
            <div className="progress-title">
              <h2>Configurador de Moodle</h2>
            </div>
            <button className="wizard-cancel" onClick={handleCancel}>
              <X size={20} />
              <span>Cancelar</span>
            </button>
          </div>
          <div className="progress-steps">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`progress-step ${
                  index < currentStep ? 'completed' : 
                  index === currentStep ? 'active' : 'pending'
                }`}
              >
                <div className="step-circle">
                  {index < currentStep ? (
                    <Check size={16} />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                <div className="step-info">
                  <span className="step-title">{step.title}</span>
                  <span className="step-description">{step.description}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="wizard-page-content">
        <div className="container">
          <React.Suspense fallback={<div className="wizard-loading">Cargando...</div>}>
            {CurrentStepComponent && (
              <CurrentStepComponent
                data={wizardData}
                onUpdate={updateWizardData}
                onNext={goToNextStep}
                onPrevious={goToPreviousStep}
              />
            )}
          </React.Suspense>
        </div>
      </div>

      {/* Footer */}
      <div className="wizard-page-footer" style={{ display: 'none' }}>
        <div className="container">
          <div className="wizard-page-footer-content">
            <div className="footer-left">
              <span className="step-counter">
                Paso {currentStep + 1} de {steps.length}
              </span>
            </div>
            <div className="footer-right">
              <button
                className="btn btn-secondary"
                onClick={goToPreviousStep}
                disabled={currentStep === 0}
              >
                <ChevronLeft size={18} />
                Anterior
              </button>
              {currentStep === steps.length - 1 ? (
                <button
                  className="btn btn-primary"
                  onClick={handleComplete}
                >
                  <Check size={18} />
                  Crear Moodle
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={goToNextStep}
                >
                  Siguiente
                  <ChevronRight size={18} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoodleWizardPage;